import React, { useRef, useState, forwardRef, useImperativeHandle, useEffect, useCallback, useMemo } from 'react';
import { VList } from "virtua";
import { InView } from 'react-intersection-observer';
// import { getScrollDiff, isInView } from './utils';
// import cloneDeep from 'lodash.clonedeep';
import { getDataIndex, getFirstVisible, getStartIndex } from './utils';
import "./style.scss";

const VirtualScroll = forwardRef(function VirtualScroll(props, ref) {
  const {
    data = [],
    cache,
    className = '',
    isLoading,
    renderItem = () => { },
    onItemInView = () => { },
    onScrollAtStart = () => { },
    onUnmount = () => { },
    reverse,
    loadMoreButton,
  } = props;

  const { scrollAtStart: cScrollAtStart, id: cId, offset: cOffset } = cache || {};

  const containerRef = useRef();
  const virtuaRef = useRef(ref);
  const itemsInViewRef = useRef({});
  const scrollAtStartRef = useRef(cScrollAtStart ?? true);
  const scrollAtStart = scrollAtStartRef.current;

  useImperativeHandle(ref, () => ({
    props,
    scrollToStart,
    scrollToIndex,
    scrollToID,
  }))

  const _data = useMemo(() => {
    const copy = (data || []).slice();
    return reverse ? copy.reverse() : copy;
  }, [data, reverse])

  const [init, setInit] = useState(false);
  const [fixed, setFixed] = useState(false);

  const defaultStartIndex = reverse ? _data.length - 1 : 0;
  const startIndex = getStartIndex(_data, reverse, cId, scrollAtStart, init, defaultStartIndex);
  const firstVisibleRef = useRef({
    id: cId ?? _data[startIndex]?.id,
    offset: cOffset ?? 0,
  });
  const firstVisible = firstVisibleRef.current;
  const firstVisibleIndex = getDataIndex(_data, firstVisible.id, reverse);

  const handleScroll = (offset) => {
    if (virtuaRef.current) {
      if (init) {
        const { scrollSize, viewportSize } = virtuaRef.current;
        const _scrollAtStart = reverse ? offset - scrollSize + viewportSize >= -1.5 : !offset;
        scrollAtStartRef.current = _scrollAtStart;
        onScrollAtStart(_scrollAtStart);
      } else {
        setInit(true)
      }
    }
  }

  const handleItemInView = (item, element, inView) => {
    if (!init) return;

    if (inView) {
      onItemInView(item);
      const index = getDataIndex(_data, item.id);
      itemsInViewRef.current[item.id] = { index, element };
    } else {
      delete itemsInViewRef.current[item.id];
    }

    const itemsEntries = Object.entries(itemsInViewRef.current);

    if (itemsEntries.length) {
      const firstEntry = itemsEntries.sort((a, b) => a[1].index - b[1].index)[0];
      if (Array.isArray(firstEntry)) {
        firstVisibleRef.current = getFirstVisible(parseInt(firstEntry[0]), containerRef.current, firstEntry[1].element)
      }
    }
  }

  const scrollToStart = (force) => {
    scrollToIndex(startIndex)
    if (force) {
      setFixed(true)
      setTimeout(() => setFixed(false), 300)
    }
  };

  const scrollToIndex = (index, align = 'start') => {
    if (!virtuaRef.current) return;
    virtuaRef.current.scrollToIndex(index, { align })
  };

  const scrollToID = (id) => scrollToIndex(getDataIndex(_data, id, reverse));

  const keepScroll = () => {
    if (!virtuaRef.current || firstVisibleIndex < 0) return;
    virtuaRef.current.scrollTo(virtuaRef.current.getItemOffset(firstVisibleIndex) + firstVisible.offset)
  }

  useEffect(() => {
    if (scrollAtStart || fixed) {
      scrollToStart();
    } else {
      keepScroll();
    }
  }, [_data.length])

  useEffect(() => {
    scrollToStart();
    return () => onUnmount({ ...firstVisibleRef.current, scrollAtStart: scrollAtStartRef.current })
  }, []);

  return <div style={{ height: '100%', width: '100%', position: 'relative' }} ref={containerRef}>
    <VList
      ref={virtuaRef}
      style={{ height: '100%', overflowX: 'hidden' }}
      className={["scrollable", className].join(' ')}
      onScroll={handleScroll}
      reverse={reverse}
      shift={isLoading}
    >
      {reverse && loadMoreButton}
      {_data.map((item) => <Item
        key={item.id}
        content={renderItem(item)}
        onView={(itemElement, inView) => handleItemInView(item, itemElement, inView)}
      />
      )}
      {!reverse && loadMoreButton}
    </VList>
  </div>
})

export default VirtualScroll;

const Item = ({ content, onView }) => {

  useEffect(() => {
    return () => onView(null, false);
  }, [])

  return (
    <InView
      onChange={(inView, entry) => onView(entry.target, inView)}
      threshold={0.75}
    >
      {content}
    </InView>
  )
}

// const VirtualScroll = forwardRef(function VirtualScroll(props, ref) {
//   const {
//     data = [],
//     className = "",
//     reversed,
//     lastScrollData = { scrollTop: 0, offset: 0 },
//     renderItem = () => { },
//     onMessageInView = () => { },
//     onLastScroll = () => { },
//     onScrollAtStart = () => { },
//     minItemHeight = 50,
//     buffer = 10,
//     length = 10,
//     loadMoreButton,
//     ...rest
//   } = props;

//   const _data = useMemo(() => (reversed ? cloneDeep(data).reverse() : data), [reversed, data]);

//   const totalLength = data.length;
//   const scrollerRef = useRef(null);
//   const scroller = scrollerRef.current;
//   const itemsRef = useRef([]);
//   const lastVisibleItemsRef = useRef([]);
//   const lastScrollTopRef = useRef(lastScrollData.scrollTop);
//   const avgRowHeight = useRef(lastScrollData.rowHeight || minItemHeight);

//   const [init, setInit] = useState(false);
//   const [restored, setRestored] = useState(false);
//   const offsetRef = useRef(lastScrollData.offset);
//   const [offset, setOffset] = useState(offsetRef.current);
//   const updateOffset = (value) => {
//     offsetRef.current = value;
//     setOffset(value);
//   }
//   const [scrollAtStart, setScrollAtStart] = useState(Math.abs(lastScrollTopRef.current) <= 5);

//   const start = Math.max(0, offset - buffer);
//   const end = Math.min(offset + (length + buffer), totalLength);
//   const topPadding = Math.max(0, start * avgRowHeight.current);
//   const bottomPadding = Math.max(0, (totalLength - end) * avgRowHeight.current);

//   const scrollToIndex = (index) => {
//     if (!scroller) return;
//     updateOffset(Math.max(0, index - length));
//     const top = Math.max(0, index - 2) * avgRowHeight.current;
//     scroller.scroll({ top: reversed ? -top : top });
//   };

//   const scrollToID = (id, options) => {
//     if (!scroller) return;
//     const index = _data.findIndex((item) => id === item.id);
//     if (index >= 0) {
//       updateOffset(Math.max(0, index - length));
//       setTimeout(() => {
//         const item = itemsRef.current.find(({ props }) => props['data-id'] === id);
//         if (item && item.node) {
//           scroller.scroll({ top: item.node.offsetTop - (options?.centered ? scroller.offsetHeight / 2 : 0) });
//         }
//       }, 100);
//     }
//   };

//   const scrollToStart = () => {
//     if (!scroller) return;
//     updateOffset(0);
//     scroller.scroll({ top: 0 });
//   };

//   const handleScroll = (e) => {
//     if (!scroller) return;

//     const scrollTop = Math.abs(scroller.scrollTop);

//     if (scrollTop === scroller.scrollHeight - scroller.offsetHeight) {
//       updateOffset(Math.max(0, totalLength - length));
//     } else {
//       const direction = Math.floor(scrollTop - lastScrollTopRef.current);

//       if (direction === 0) return;

//       const items = itemsRef.current.map(item => item?.node);
//       let newOffset = offset;
//       let newAvgRowHeight = avgRowHeight.current;
//       const start = Math.min(offset, buffer);
//       if (direction > 0) {
//         if (offset < totalLength - length) {
//           let heightAdded = 0;
//           for (let i = start; i < items.length; i++) {
//             if (items[i]) {
//               const inView = isInView(scroller, items[i], reversed);
//               const rowHeight = items[i].clientHeight;
//               if (!inView) {
//                 heightAdded += rowHeight;
//                 newOffset++;
//               } else {
//                 break;
//               }
//             }
//           }

//           updateOffset(Math.min(newOffset, totalLength - length));
//           avgRowHeight.current = Math.max(minItemHeight, newAvgRowHeight);
//         }
//       } else if (items[start]) {
//         const scrollDiff = getScrollDiff(scroller, items[start], reversed);
//         if (scrollDiff > 0) {
//           const offsetDiff = Math.floor(scrollDiff / minItemHeight) || 1;
//           const newOffset = offset - offsetDiff;
//           if (newOffset < totalLength - (length + buffer)) {
//             updateOffset(Math.max(0, newOffset));
//           }
//         }
//       }
//     }

//     lastScrollTopRef.current = scrollTop;
//     setScrollAtStart(scrollTop <= 5);
//   }

//   useEffect(() => {
//     if (scroller) {
//       if (lastScrollData) {
//         const { scrollTop, rowHeight, lastVisibleID } = lastScrollData;
//         // if (scrollTop && lastVisibleID) { //Если в сохраненных метаданных скроллера для чата есть scrollTop, восстанавливаем его
//         if (lastVisibleID) {
//           avgRowHeight.current = rowHeight || minItemHeight;
//           scrollToID(lastVisibleID);
//           setRestored(true);
//         } else {
//           lastScrollTopRef.current = 0;
//           avgRowHeight.current = minItemHeight;
//           scroller.scroll({ top: 0 });
//           updateOffset(0);
//           setRestored(true)
//         }
//       } else {
//         setRestored(true)
//       }
//     }

//     return () => {
//       onLastScroll({
//         scrollTop: lastScrollTopRef.current,
//         rowHeight: avgRowHeight.current,
//         offset: offsetRef.current,
//         lastVisibleID: lastVisibleItemsRef.current.pop(),
//       });
//       setRestored(false);
//     };
//   }, [scroller])

//   useEffect(() => {
//     onScrollAtStart(scrollAtStart)
//   }, [scrollAtStart])

//   useImperativeHandle(ref, () => ({
//     props,
//     scrollToStart,
//     scrollToIndex,
//     scrollToID,
//   }));

//   const handleVisible = useCallback((message, inView) => {
//     if (inView && restored) {
//       onMessageInView(message)
//     }
//     let lastVisible = [...lastVisibleItemsRef.current];
//     if (inView) {
//       lastVisible.push(message.id)
//     } else if (lastVisible.includes(message.id)) {
//       lastVisible = lastVisible.filter(id => id !== message.id)
//     }
//     lastVisibleItemsRef.current = [...new Set(lastVisible)]
//   }, [restored, lastVisibleItemsRef.current])

//   return <div
//     {...rest}
//     ref={(el) => {
//       scrollerRef.current = el;
//       if (!init) {
//         setInit(true)
//       };
//     }}
//     className={['scrollable vs-container', reversed ? 'reversed' : '', className].join(' ')}
//     onScroll={handleScroll}
//   >
//     {init && (
//       <>
//         <div
//           id="top-padding"
//           className='vs-padding'
//           style={{
//             height: topPadding
//           }}
//         />
//         {_data.slice(start, end).map((message, index) => message && <InView
//           key={message.id}
//           data-id={message.id}
//           ref={ref => itemsRef.current[index] = ref}
//           onChange={(inView) => handleVisible(message, inView)}
//           threshold={1}
//         >
//           {renderItem(message)}
//         </InView>)}
//         <div
//           id='bottom-padding'
//           className='vs-padding'
//           style={{
//             height: bottomPadding
//           }}
//         />
//         {loadMoreButton}
//       </>
//     )}
//   </div>
// })


